import { AppProtocolColumn, AppProtocolRaw } from '@kentik/ui-shared/types/lookup.types';
import { RequiredPermissions } from '@kentik/ui-shared/dictionary/v2/compilers/types';
import { getAppProtocolColKey } from '@kentik/ui-shared/dictionary/v2/compilers/util';

export interface AppProtocolsQueryParams {
  appProtocols: AppProtocolRaw[];
  permissions: RequiredPermissions | false;
  // Pass this field if you want to ONLY return the fields that require specific permissions to view
  // (i.e. you have already acquired the base set of app protocol information)
  additivePermissionsOnly?: true;
}

export interface AppProtocolColContext {
  protocol: AppProtocolRaw;
  column: AppProtocolColumn;
  colKey: string;
  treatAsMetric: boolean;
  treatAsDimension: boolean;
}

export type AppProtocols = {
  all: AppProtocolColContext[];
  metric: AppProtocolColContext[];
  dimension: AppProtocolColContext[];
  lookup: AppProtocolColContext[];
};

/**
 * Converts nested appProtocol columns into a single flat array, with pre-computed values for common lookups.
 */
export const flattenAppProtocolColumns = (appProtocols: AppProtocolRaw[]): AppProtocols => {
  const results: AppProtocols = { all: [], metric: [], dimension: [], lookup: [] };
  for (const protocol of appProtocols) {
    for (const column of protocol.columns) {
      const isMetricStrict = column.metadata.is_metric === true;
      const result = {
        protocol,
        column,
        colKey: getAppProtocolColKey(protocol, column),
        treatAsMetric: isMetricStrict || column.metadata.hll === true,
        treatAsDimension: !isMetricStrict,
        hasLookup: column.metadata.lookup
      };
      results.all.push(result);
      if (result.treatAsDimension) {
        results.dimension.push(result);
      }
      if (result.treatAsMetric) {
        results.metric.push(result);
      }
      if (result.hasLookup) {
        results.lookup.push(result);
      }
    }
  }
  return results;
};

/**
 * Filters appProtocols by permission, returning ONLY the app protocols that specifically require permissions.
 * For use when augmenting an existing appProtocols collection.
 */
const inverseFilterAppProtocolsByPermissions = (
  appProtocols: AppProtocolRaw[],
  permissions: RequiredPermissions
): AppProtocolRaw[] => {
  const allowEvents = !!permissions.alerts?.eventAlerting?.enabled;
  const allowSynthetics = !!permissions.synthetics?.dataExplorer?.enabled;
  if (!allowEvents && !allowSynthetics) {
    return [];
  }
  // ONLY return appProtocols that require specific permissions
  return appProtocols.filter((p) => {
    if (allowEvents && (p.app_protocol === 'event_syslog' || p.app_protocol.startsWith('event_'))) {
      return true;
    }
    return allowSynthetics && p.app_protocol === 'synag';
  });
};

/**
 * Filters appProtocols by permission, excluding unpermitted protocols.
 */
const filterAppProtocolsByPermissions = (
  appProtocols: AppProtocolRaw[],
  permissions: RequiredPermissions | false
): AppProtocolRaw[] => {
  const allowEvents = permissions && !!permissions.alerts?.eventAlerting?.enabled;
  const allowSynthetics = permissions && !!permissions.synthetics?.dataExplorer?.enabled;

  return appProtocols.filter((p) => {
    // filter out events if eventAlerting is disabled
    if (!allowEvents && (p.app_protocol === 'event_syslog' || p.app_protocol.startsWith('event_'))) {
      return false;
    }
    // filter out synthetics if synthetics is disabled
    return allowSynthetics || p.app_protocol !== 'synag';
  });
};

/**
 * Filters appProtocols based on permissions.
 *
 * Pass `additivePermissionsOnly` to return ONLY the protocols that specifically require permissions or permissions
 * of `false` to exclude ALL protocols that require permissions.
 *
 * Merging the results of those two operations produces the same value as you would achieve with an ordinary invocation of
 * this function - this means that you can memoize this function in multiple stages or as a single step.
 */
export const filterAppProtocols = ({ permissions, appProtocols, additivePermissionsOnly }: AppProtocolsQueryParams) => {
  return additivePermissionsOnly && permissions
    ? inverseFilterAppProtocolsByPermissions(appProtocols, permissions)
    : filterAppProtocolsByPermissions(appProtocols, permissions);
};
